import { useState } from 'react';
import { Copy, Check, ArrowSquareOut } from '@phosphor-icons/react';
import { GuideSection } from '@/components/guides/GuideSection';
import { GuideCallout } from '@/components/guides/GuideCallout';
import { useBranding } from '@/lib/branding/BrandingProvider';

export function ShareCandidateGuideSection() {
  const brand = useBranding();
  const [copied, setCopied] = useState(false);

  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : brand.domain
        ? `https://${brand.domain}`
        : '';
  const candidateUrl = `${origin}/candidate-guide`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(candidateUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  return (
    <GuideSection
      id="candidate-guide"
      eyebrow="For your candidates"
      title="Share the candidate guide"
      description="A branded walkthrough your candidates can read before their first interview. Hosted publicly on your portal domain — no login needed."
    >
      <div className="glass-card relative overflow-hidden p-5">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-neurax-plasma/40 via-neurax-electric/40 to-transparent" />
        <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-neurax-plasma/80">
          Shareable link
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Send this URL to a candidate via email, Slack, or anywhere else. They'll see a branded
          page with step-by-step instructions for installing NeuraX, entering your company code, and
          running it during their interview.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <code className="flex-1 truncate rounded-xl border border-white/10 bg-black/30 px-3 py-2 font-mono text-xs text-white">
            {candidateUrl}
          </code>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white transition hover:border-neurax-electric/40 hover:bg-neurax-electric/10"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 text-neurax-mint" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy link
              </>
            )}
          </button>
          <a
            href="/candidate-guide"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white transition hover:border-neurax-electric/40 hover:bg-neurax-electric/10"
          >
            <ArrowSquareOut className="h-3.5 w-3.5" />
            Preview
          </a>
        </div>
      </div>

      <GuideCallout variant="info" title="What's on the candidate guide">
        <ul className="ml-4 list-disc space-y-1">
          <li>What NeuraX is and what they'll need to get started.</li>
          <li>How to install the desktop app on macOS or Windows.</li>
          <li>First-time setup: entering the company code, granting permissions.</li>
          <li>How to connect to their interview session using the license key.</li>
          <li>What to do during the interview — positioning, reading the coaching feed.</li>
          <li>Privacy expectations — what you (the admin) can see.</li>
          <li>Troubleshooting the common failure modes.</li>
        </ul>
      </GuideCallout>

      <GuideCallout variant="tip" title="When to share it">
        <p>
          Include the link in your first outreach email to a candidate — same message where you
          share the company code and the license key. Candidates who read it before installing
          NeuraX spend noticeably less time on setup questions.
        </p>
      </GuideCallout>
    </GuideSection>
  );
}
